import React, { createContext, useContext, useState, useCallback } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]   = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const [token, setToken] = useState(() => localStorage.getItem('token') || '');

  const login = useCallback(async (credentials) => {
    const { data } = await authService.login(credentials);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authService.logout(); } catch (_) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken('');
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedUser) => {
    // Preserve permissions/role if a partial update (e.g. profile) omits them.
    setUser((prev) => {
      const merged = {
        ...updatedUser,
        permissions: updatedUser.permissions ?? prev?.permissions,
        role:        updatedUser.role ?? prev?.role,
        is_super_admin: updatedUser.is_super_admin ?? prev?.is_super_admin,
      };
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    });
  }, []);

  // Permission check — Super Admin passes everything.
  const can = useCallback((permission) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    const perms = user.permissions || [];
    if (Array.isArray(permission)) {
      return permission.some((p) => perms.includes(p));
    }
    return perms.includes(permission);
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user, token, login, logout, updateUser, can,
      isAuthenticated: !!token,
      isSuperAdmin: !!user?.is_super_admin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
